import React, { useContext, useState } from 'react';
import { Popover } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import AsperLogo from 'assets/svg/AsperLogo';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import NavBarOptions from 'components/molecules/NavBarOptions';
import _ from 'lodash';
import TimeStampNavbar from 'components/molecules/TimeStampNavbar';
import { extractEmailAndName, getAuthUser, getConfig } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { USER_MANUAL_CLICKED } from 'utils/mixpanelEventNames';
import CustomAsperButton from 'components/atoms/CustomAsperButton';
import { BellOutlined } from '@ant-design/icons';
import UserAccountManager from 'components/molecules/common/UserAccountManager';
import { isAdminUser } from 'constants/admin';
import { getTenantLogo } from 'utils/tenantLogos';

// this key mapping will be used to enable the user role based feature access
const keyMapping = [
    { id: 'ga', name: 'growth-analysis' },
    { id: 'ps', name: 'promo-score-card' },
    { id: 'br', name: 'bump-report' },
    { id: 'pa', name: 'portfolio-analysis, roi analysis' },
    { id: 'pl', name: 'promo-landscape,roi tracker' },
    { id: 'pld', name: 'price-ladder' },
    { id: 'oi', name: 'opportunity-identification' },
    { id: 'pc', name: 'price-curve' },
    { id: 'pcn', name: 'price-change' },
    { id: 'pp', name: 'price-point' },
    { id: 'pr', name: 'promo-recommendation' },
    { id: 'pt', name: 'price-tracking' },
    { id: 'psr', name: 'price-simulator' },
    { id: 'da', name: 'depletion analysis' },
    { id: 'pvm', name: 'PVM analysis' },
];

interface props {
    logOut: any;
}
function Navbar({ logOut }: props) {
    let location = useLocation();
    const pathname = location.pathname;

    const [current, setCurrent] = useState('');
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const showLogo = commonConfig?.showLogoInNavbar || false;
    const logoHeight = commonConfig?.logoHeight;
    const logoMaxWidth = commonConfig?.logoWidth;
    const tenantId = process.env.REACT_APP_TENANT_ID || '';
    const navbarLogo = getTenantLogo(tenantId);
    const screens = getConfig('screens', appConfig);
    const [navOptions, setNavOptions] = useState<any>(filterScreensByUserRoleAccessAndTenantSettings());
    const [profileWindow, setProfileWindow] = useState(false);

    const pattern = /([^\/]+$)/;
    const pathInfo = pattern.exec(location.pathname);

    function mouseOverListVisible(navOptions: any, path: string) {
        let temp = _.cloneDeep(navOptions);
        Object.keys(temp).forEach((listOptions: any) => {
            if (temp[listOptions].label === path) {
                temp[listOptions].options.visible = !temp[listOptions].options.visible;
            } else {
                temp[listOptions].options.visible = false;
            }
        });
        setNavOptions(temp);
    }
    const navigate = useNavigate();
    //user name in short form
    const userName = getAuthUser();
    const fullName = extractEmailAndName();

    function filterScreensByUserRoleAccessAndTenantSettings() {
        // use role access filter
        const userScope = JSON.parse(localStorage.getItem('userAccessScope') || '{}');
        const accessbileScreens = userScope?.data_metrics?.forecast_sources?.screens;
        const filteredScreensByUserRole = filterScreensByAccessibility(screens, accessbileScreens);

        //  config level access filter, Note: pass the above result to the second function
        const configlevelAccessScreens = appConfig.commonConfig.accessibleScreens;
        const filteredResult = filterScreensByAccessibility(filteredScreensByUserRole, configlevelAccessScreens);

        return filteredResult;
    }

    function filterScreensByAccessibility(allAvailableScreens: any, accessbileScreens: any) {
        // NOTE: if screens property is not present or an empty array, All the screens will be accessible
        if (!accessbileScreens || accessbileScreens?.length === 0) return allAvailableScreens;

        const filteredScreens: any = [];
        allAvailableScreens.forEach((navLink: any) => {
            const newNavLink: any = {
                label: navLink.label,
                path: navLink.path,
                icon: navLink.icon,
            };

            // Handle new config options
            if (navLink.options && Array.isArray(navLink.options)) {
                const filteredOptions = navLink.options
                    .map((option: any) => {
                        const filteredItems = option.items?.filter((item: any) => accessbileScreens.includes(item?.id));
                        if (filteredItems && filteredItems.length > 0) {
                            return {
                                ...option,
                                items: filteredItems,
                            };
                        }
                        return null;
                    })
                    .filter(Boolean); // Remove null options

                if (filteredOptions.length > 0) {
                    newNavLink.options = filteredOptions;
                    filteredScreens.push(newNavLink);
                }
            }
            // Handle old options before demo
            else if (navLink.options && typeof navLink.options === 'object') {
                const newOptions: any = {};
                Object.keys(navLink.options).forEach((key) => {
                    const labelPathPairs = navLink.options[key];
                    if (Array.isArray(labelPathPairs)) {
                        const newPaths = labelPathPairs.filter((labelPair) =>
                            accessbileScreens.includes(labelPair?.id),
                        );
                        if (newPaths.length) {
                            newOptions[key] = newPaths;
                        }
                    }
                });

                // take the navLink only when it has child links present
                if (Object.keys(newOptions).length) {
                    newOptions.visible = false;
                    newNavLink.options = newOptions;
                    filteredScreens.push(newNavLink);
                }
            }
        });

        return filteredScreens;
    }

    function openProfile(profileWindow: boolean, setProfileWindow: Function) {
        setProfileWindow(!profileWindow);
    }

    const adminPath = `/admin`;
    const content = () => {
        return (
            <div className="bg-white min-w-[12rem] rounded-b-lg font-medium font-xs">
                <div className="text-purpleFont mb-2 px-1">{fullName}</div>
                {isAdminUser() && (
                    <Link
                        to={adminPath}
                        className="flex items-center gap-2 w-full px-3 py-2 mt-2 text-fontColor border-y border-borderGrey hover:font-bold"
                    >
                        <SettingOutlined className="text-fontColor" />
                        Settings
                    </Link>
                )}
                <div
                    className="w-full py-1 mt-4 bg-purpleFont rounded flex justify-center items-center cursor-pointer"
                    onClick={() => logOut()}
                >
                    <span className="text-white">Logout</span>
                </div>
            </div>
        );
    };

    return (
        <nav
            id="main-navbar"
            className="navbar-height z-[12] w-full border border-borderLinePurpule font-md flex items-center justify-between px-4 fixed bg-white"
        >
            <div className="flex items-center">
                {/* <AsperLogo /> */}
                {showLogo && (
                    <div className="global-filter-width mr-3 ">
                        <img style={{ maxWidth: logoMaxWidth, height: logoHeight }} src={navbarLogo} />
                    </div>
                )}
                <NavBarOptions
                    navOptions={navOptions}
                    mouseOverListVisible={mouseOverListVisible}
                    pathInfo={pathInfo}
                />
                {process.env.REACT_APP_FRESHDESK_MANUAL_URL !== 'null' && (
                    <a
                        onClick={() => {
                            trackEventMixpanel(USER_MANUAL_CLICKED);
                        }}
                        target="_blank"
                        href={process.env.REACT_APP_FRESHDESK_MANUAL_URL}
                        className="font-md text-purpleFont hover:text-purpleFont ml-4"
                    >
                        {'User Manual'}
                    </a>
                )}
            </div>
            <div className="flex justify-center gap-3 items-center">
                <CustomAsperButton
                    onClick={() => {
                        navigate('/ask-asper');
                    }}
                />
                <span className="flex justify-center items-center gap-x-7">
                    <TimeStampNavbar />
                </span>
                <Link to="/alert">
                    <span
                        className={`flex justify-center items-center text-purpleFont hover:bg-bgBackground gap-x-7 p-1.5 rounded ${
                            pathname === '/alert' ? 'bg-bgBackground' : ''
                        }`}
                    >
                        <BellOutlined />
                    </span>
                </Link>
                {/* commented this one for demo purpose */}
                {/* <span className="relative">
                    <Popover
                        overlayInnerStyle={{ position: 'relative', top: -5 }}
                        trigger={'hover'}
                        showArrow={false}
                        placement="bottomRight"
                        content={content}
                    >
                        <span
                            className="flex justify-center items-center rounded-full cursor-pointer bg-bgBackground avatar-size"
                            onClick={() => openProfile(profileWindow, setProfileWindow)}
                        >
                            <span className="text-center text-purpleFont font-xxs font-bold">{userName}</span>
                        </span>
                    </Popover>
                </span> */}

                {/* New User Account Manager */}
                <UserAccountManager
                    logOut={logOut}
                    avatarClassName="flex justify-center items-center rounded-full cursor-pointer bg-bgBackground avatar-size"
                    textClassName="text-center text-purpleFont font-xxs font-bold"
                />
            </div>
        </nav>
    );
}

export default Navbar;
